import { NewBookingDeliveryPropType } from 'common/prop-types';
import DeliveryContext from 'components/v2/anytime-booking/common/context';
import { codAdditionalServiceID } from 'constants/anytime-booking-constants';
import NewBookingDuck from 'ducks/new-booking-duck';
import ScribeDuck from 'ducks/scribe-duck';
import { Form } from 'grab-ui';
import { isAreaZoningHardBlock } from 'helper/error-codes';
import I18n from 'i18n-js';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
import copyIcon from 'public/icons/copy.svg';
import deleteIcon from 'public/icons/delete.svg';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { connect, useSelector } from 'react-redux';
import {
  compulsoryCODSelector,
  enableDeliveryGuaranteeSelector,
  userInfoSelector,
} from 'selectors/app-selector';
import { deliverySelector } from 'selectors/new-booking-selectors';

import AdditionalServices from './additional-services';
import {
  deliveryFormFields,
  isOtherDeliveryDetailField,
  isStepDetailField,
  parseStepDetailField,
} from './common';
import DeliveryRoute from './delivery-route';
import InsuranceConfigurations from './insurance-configurations';
import ItemDetails from './item-details';
import PaymentDetails from './payment-details';
import ServiceConfigurations from './service-configurations';
import styles from './styles.less';

const DeliveryForm = (
  {
    form,
    dispatch,
    index,
    deliveryID,
    delivery,
    canDelete,
    canCopy,
    compulsoryCOD,
  },
  ref,
) => {
  // forward form functions via ref
  useImperativeHandle(ref, () => ({
    getFieldsValue: form.getFieldsValue,
    validateFields: form.validateFields,
    getFieldsError: form.getFieldsError,
  }));

  // when rebook, will only fill the cod field, but not COD additional service
  const [validateRebookDeliveryOnce, setValidateRebookDeliveryOnce] = useState(
    false,
  );
  useEffect(
    () => {
      if (
        delivery.isRebook &&
        !validateRebookDeliveryOnce &&
        delivery.config &&
        form
      ) {
        setValidateRebookDeliveryOnce(true);
        form.validateFields();
      }
    },
    [
      delivery.isRebook,
      delivery.config,
      form,
      validateRebookDeliveryOnce,
      setValidateRebookDeliveryOnce,
    ],
  );

  const onDeleteDelivery = useCallback(
    () => {
      dispatch(NewBookingDuck.creators.deleteDelivery(delivery.id));
      dispatch(
        ScribeDuck.creators.scribeExpressDeliveryDetailDeleteDelivery(index),
      );
    },
    [dispatch, deliveryID],
  );

  const onCopyDelivery = useCallback(
    () => {
      dispatch(NewBookingDuck.creators.copyDelivery(delivery.id));
      dispatch(
        ScribeDuck.creators.scribeExpressDeliveryDetailDuplicateDelivery(index),
      );
    },
    [dispatch, deliveryID],
  );

  const enableInsuranceFee = useSelector(enableDeliveryGuaranteeSelector);

  // Check if delivery has hard block error
  const paxExpressError = _get(delivery, 'paxExpressError');
  const hasHardBlockError =
    paxExpressError && isAreaZoningHardBlock(paxExpressError.errorCode);

  return (
    <DeliveryContext.Provider value={delivery}>
      <div className={styles.deliveryForm}>
        <div className={styles.deliveryTitleRow}>
          <div className={styles.deliveryTitle}>
            {I18n.t('v2.homepage.manualOrder.heading.deliveryNo', {
              no: index + 1,
            })}
            <span className={styles.deliveryIDHidden}> {delivery.id} </span>
          </div>
          <div className={styles.deliveryActions}>
            {canDelete && (
              <a className={styles.deliveryAction} onClick={onDeleteDelivery}>
                <img
                  src={deleteIcon}
                  alt={'delete'}
                  className={styles.deleteIcon}
                />
              </a>
            )}
            {canCopy && (
              <a className={styles.deliveryAction} onClick={onCopyDelivery}>
                <img src={copyIcon} alt={'copy'} />
              </a>
            )}
          </div>
        </div>

        <div className={styles.sections}>
          <DeliveryRoute
            form={form}
            deliveryIndex={index}
            delivery={delivery}
            compulsoryCOD={compulsoryCOD}
            dispatch={dispatch}
          />
          <ItemDetails
            form={form}
            delivery={delivery}
            disabled={hasHardBlockError}
          />
          <ServiceConfigurations
            form={form}
            delivery={delivery}
            dispatch={dispatch}
            deliveryIndex={index}
            disabled={hasHardBlockError}
          />
          {enableInsuranceFee &&
            delivery.quote && (
              <InsuranceConfigurations
                delivery={delivery}
                disabled={hasHardBlockError}
              />
            )}
          <AdditionalServices
            delivery={delivery}
            disabled={hasHardBlockError}
          />
          <PaymentDetails
            form={form}
            delivery={delivery}
            index={index}
            disabled={hasHardBlockError}
          />
        </div>
      </div>
    </DeliveryContext.Provider>
  );
};

DeliveryForm.defaultProps = {
  canDelete: false,
  canCopy: true,
  compulsoryCOD: false,
};

DeliveryForm.propTypes = {
  delivery: NewBookingDeliveryPropType.isRequired,
  deliveryID: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  form: PropTypes.shape({}).isRequired,
  index: PropTypes.number.isRequired,
  canDelete: PropTypes.bool,
  canCopy: PropTypes.bool,
  compulsoryCOD: PropTypes.bool,
};

const onFieldsChange = ({ dispatch, delivery }, changedFields, allValues) => {
  // update service configs
  const vehicleType = _get(
    changedFields,
    `${deliveryFormFields.vehicleType.fieldName}.value`,
  );

  if (vehicleType) {
    dispatch(
      NewBookingDuck.creators.updateServiceConfig(delivery.id, { vehicleType }),
    );

    dispatch(ScribeDuck.creators.scribeExpressDeliveryVehicleType(vehicleType));
  }

  // update delivery step fields
  Object.entries(changedFields).map(([fn, fv]) => {
    if (isStepDetailField(fn)) {
      const { isPickUp, index, fieldName } = parseStepDetailField(fn);
      dispatch(
        NewBookingDuck.creators.updateDeliveryStep(
          delivery.id,
          isPickUp,
          index,
          fieldName,
          fv.value,
        ),
      );

      if (fieldName === deliveryFormFields.cod.fieldName) {
        if (
          _get(fv, 'errors[0]') ||
          !fv.value ||
          fv.value === '0' ||
          fv.value === 0
        ) {
          dispatch(
            NewBookingDuck.creators.removeAdditionalService(
              delivery.id,
              codAdditionalServiceID,
            ),
          );
        } else {
          dispatch(
            NewBookingDuck.creators.addAdditionalService(delivery.id, {
              id: codAdditionalServiceID,
              data: {
                enabled: true,
              },
            }),
          );
        }
      }
      dispatch(
        ScribeDuck.creators.scribeExpressDeliveryDetailFormChange({
          changedFields,
          allValues,
          index,
        }),
      );
    }
  });

  // update delivery detail fields
  Object.entries(changedFields).map(([fn, fv]) => {
    if (isOtherDeliveryDetailField(fn)) {
      dispatch(
        NewBookingDuck.creators.updateDeliveryDetail(delivery.id, fn, fv.value),
      );

      dispatch(
        ScribeDuck.creators.scribeExpressDeliveryDetailFormChange({
          changedFields,
          allValues,
        }),
      );
    }
  });

  const currentItemType = allValues[deliveryFormFields.itemType.fieldName];
  const itemTypeFreeTextChange =
    changedFields[deliveryFormFields.itemTypeFreeText.fieldName];

  if (
    currentItemType &&
    currentItemType.value &&
    itemTypeFreeTextChange &&
    itemTypeFreeTextChange.value
  ) {
    if (currentItemType.value.startsWith('7-')) {
      const itemTypeFreeTextValue = itemTypeFreeTextChange.value.trim();
      const formattedValue =
        itemTypeFreeTextValue.charAt(0).toUpperCase() +
        itemTypeFreeTextValue.slice(1).toLowerCase();

      dispatch(
        NewBookingDuck.creators.updateDeliveryDetail(
          delivery.id,
          deliveryFormFields.itemType.fieldName,
          `20001-${formattedValue}`,
        ),
      );
    }
  }
};

export default connect(
  (state, props) => ({
    userInfo: userInfoSelector(state),
    delivery: deliverySelector(state, props),
    compulsoryCOD: compulsoryCODSelector(state),
  }),
  null,
  null,
  { forwardRef: true },
)(Form.create({ onFieldsChange })(forwardRef(DeliveryForm)));
